/**
 * CartDrawer — right-side elegant bag drawer.
 *
 * Mounted globally in the Layout so it can be triggered from anywhere via
 * CartContext.openDrawer(). Preserves the existing /cart page as a fallback.
 */
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useCart } from "@/context/CartContext";
import api, { formatPrice } from "@/lib/api";
import { Minus, Plus, X, ShoppingBag, ArrowRight, Truck } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { motion, AnimatePresence } from "framer-motion";

const FREE_SHIP_THRESHOLD = 200000; // ₹2,000 in paise

export default function CartDrawer() {
    const { drawerOpen, setDrawerOpen, closeDrawer, preview, items, updateQty, removeItem } = useCart();
    const navigate = useNavigate();
    const [upsells, setUpsells] = useState([]);

    useEffect(() => {
        if (!drawerOpen) return;
        api.get("/catalog/products?featured=true&limit=6")
            .then((r) => setUpsells(r.data.items || []))
            .catch(() => setUpsells([]));
    }, [drawerOpen]);

    const subtotal = preview?.subtotal || 0;
    const shipping = preview?.shipping || 0;
    const total = preview?.total || 0;
    const progress = Math.min(100, Math.round((subtotal / FREE_SHIP_THRESHOLD) * 100));
    const remaining = Math.max(0, FREE_SHIP_THRESHOLD - subtotal);

    const previewMap = useMemo(() => {
        const m = {};
        for (const it of preview?.items || []) {
            m[`${it.product_id}::${it.variant_sku}`] = it;
        }
        return m;
    }, [preview]);

    const goCheckout = () => {
        closeDrawer();
        navigate("/checkout");
    };

    return (
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetContent
                side="right"
                className="w-full sm:w-[440px] p-0 border-l border-border bg-background flex flex-col"
                data-testid="cart-drawer"
            >
                <SheetHeader className="px-5 sm:px-7 pt-6 pb-4 border-b border-border">
                    <div className="flex items-center gap-3">
                        <ShoppingBag className="h-4 w-4" />
                        <SheetTitle className="font-display text-2xl leading-none flex-1 text-left">
                            The Bag
                        </SheetTitle>
                        <span className="font-mono-ui text-muted-foreground" data-testid="cart-drawer-count">
                            {items.length} item{items.length === 1 ? "" : "s"}
                        </span>
                    </div>

                    {/* Free-shipping progress */}
                    <div className="mt-5">
                        {remaining > 0 ? (
                            <p className="text-[11px] font-mono-ui tracking-[0.14em] uppercase text-muted-foreground flex items-center gap-2">
                                <Truck className="h-3.5 w-3.5" />
                                {formatPrice(remaining)} to free shipping
                            </p>
                        ) : (
                            <p className="text-[11px] font-mono-ui tracking-[0.14em] uppercase text-[hsl(var(--accent))] flex items-center gap-2">
                                <Truck className="h-3.5 w-3.5" />
                                You've unlocked free shipping
                            </p>
                        )}
                        <Progress
                            value={progress}
                            className="h-1 mt-2 bg-secondary"
                            data-testid="free-shipping-progress"
                        />
                    </div>
                </SheetHeader>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5">
                    {items.length === 0 ? (
                        <div className="h-full min-h-[420px] flex flex-col items-center justify-center text-center">
                            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
                                <ShoppingBag className="h-6 w-6 text-muted-foreground" />
                            </div>
                            <p className="font-display text-2xl">Your bag is empty</p>
                            <p className="text-sm text-muted-foreground mt-2 max-w-xs">
                                Explore new arrivals, capsule drops, and lookbook edits.
                            </p>
                            <button
                                onClick={() => { closeDrawer(); navigate("/shop"); }}
                                className="dw-btn-primary mt-6 px-6 h-11 font-mono-ui text-xs"
                                data-testid="cart-drawer-empty-shop-button"
                            >
                                Browse the shop
                            </button>
                        </div>
                    ) : (
                        <ul className="space-y-4">
                            <AnimatePresence initial={false}>
                                {items.map((it) => {
                                    const enriched = previewMap[`${it.product_id}::${it.variant_sku}`] || {};
                                    return (
                                        <motion.li
                                            key={`${it.product_id}::${it.variant_sku}`}
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -8 }}
                                            transition={{ duration: 0.18 }}
                                            className="flex gap-4"
                                            data-testid={`cart-lineitem-${it.variant_sku}`}
                                        >
                                            <Link
                                                to={enriched.slug ? `/product/${enriched.slug}` : "#"}
                                                onClick={closeDrawer}
                                                className="h-24 w-20 flex-shrink-0 rounded-2xl bg-muted overflow-hidden"
                                            >
                                                {enriched.image && (
                                                    <img src={enriched.image} alt={enriched.title} className="h-full w-full object-cover" />
                                                )}
                                            </Link>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2">
                                                    <Link
                                                        to={enriched.slug ? `/product/${enriched.slug}` : "#"}
                                                        onClick={closeDrawer}
                                                        className="font-display text-base leading-tight truncate"
                                                    >
                                                        {enriched.title || it.variant_sku}
                                                    </Link>
                                                    <button
                                                        onClick={() => removeItem(it.product_id, it.variant_sku)}
                                                        className="h-7 w-7 rounded-full hover:bg-muted text-muted-foreground flex items-center justify-center"
                                                        aria-label="Remove"
                                                        data-testid={`cart-lineitem-remove-${it.variant_sku}`}
                                                    >
                                                        <X className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                                <p className="text-[11px] font-mono-ui tracking-[0.14em] uppercase text-muted-foreground mt-1">
                                                    {enriched.color ? `${enriched.color} · ` : ""}{enriched.size || it.variant_sku}
                                                </p>
                                                <div className="mt-3 flex items-center justify-between">
                                                    <div className="inline-flex items-center rounded-full border border-border bg-card">
                                                        <button
                                                            onClick={() => updateQty(it.product_id, it.variant_sku, it.qty - 1)}
                                                            className="h-8 w-8 flex items-center justify-center hover:bg-muted rounded-l-full"
                                                            aria-label="Decrease quantity"
                                                            data-testid={`cart-lineitem-decrement-${it.variant_sku}`}
                                                        >
                                                            <Minus className="h-3 w-3" />
                                                        </button>
                                                        <span className="px-3 text-xs font-mono-ui" data-testid={`cart-lineitem-qty-${it.variant_sku}`}>
                                                            {it.qty}
                                                        </span>
                                                        <button
                                                            onClick={() => updateQty(it.product_id, it.variant_sku, it.qty + 1)}
                                                            className="h-8 w-8 flex items-center justify-center hover:bg-muted rounded-r-full"
                                                            aria-label="Increase quantity"
                                                            data-testid={`cart-lineitem-increment-${it.variant_sku}`}
                                                        >
                                                            <Plus className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                    <p className="font-display text-base" data-testid={`cart-lineitem-total-${it.variant_sku}`}>
                                                        {formatPrice((enriched.price || 0) * it.qty)}
                                                    </p>
                                                </div>
                                            </div>
                                        </motion.li>
                                    );
                                })}
                            </AnimatePresence>
                        </ul>
                    )}

                    {/* Complete the fit upsell */}
                    {items.length > 0 && upsells.length > 0 && (
                        <div className="mt-8 pt-6 border-t border-border">
                            <p className="font-mono-ui text-muted-foreground mb-3">Complete the fit</p>
                            <div className="grid grid-cols-3 gap-3">
                                {upsells.slice(0, 3).map((p) => (
                                    <Link
                                        key={p.id}
                                        to={`/product/${p.slug}`}
                                        onClick={closeDrawer}
                                        className="group block"
                                        data-testid={`cart-upsell-${p.slug}`}
                                    >
                                        <div className="aspect-[4/5] rounded-xl overflow-hidden bg-muted">
                                            <img
                                                src={p.images?.[0]}
                                                alt={p.title}
                                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
                                            />
                                        </div>
                                        <p className="mt-2 text-[10px] font-mono-ui tracking-[0.14em] uppercase text-muted-foreground truncate">{p.category}</p>
                                        <p className="text-[11px] truncate">{p.title}</p>
                                        <p className="text-[11px] font-mono-ui">{formatPrice(p.price)}</p>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                {items.length > 0 && (
                    <div className="border-t border-border px-5 sm:px-7 py-5 pb-[calc(env(safe-area-inset-bottom)+16px)] bg-card">
                        <div className="flex items-center justify-between mb-1">
                            <span className="font-mono-ui text-muted-foreground">Subtotal</span>
                            <span className="font-mono-ui" data-testid="cart-drawer-subtotal">{formatPrice(subtotal)}</span>
                        </div>
                        <div className="flex items-center justify-between mb-3">
                            <span className="font-mono-ui text-muted-foreground">Shipping</span>
                            <span className="font-mono-ui">
                                {shipping === 0 ? "Free" : formatPrice(shipping)}
                            </span>
                        </div>
                        <div className="flex items-center justify-between border-t border-border pt-3 mb-4">
                            <span className="font-display text-lg">Total</span>
                            <span className="font-display text-2xl" data-testid="cart-drawer-total">{formatPrice(total)}</span>
                        </div>
                        <button
                            onClick={goCheckout}
                            className="dw-btn-primary w-full h-12 font-mono-ui text-xs inline-flex items-center justify-center gap-2"
                            data-testid="cart-drawer-checkout-button"
                        >
                            Checkout <ArrowRight className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => { closeDrawer(); navigate("/cart"); }}
                            className="w-full mt-2 h-11 font-mono-ui text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                            data-testid="cart-drawer-view-cart-button"
                        >
                            View full bag
                        </button>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
